import React, { useState, useEffect, useRef } from "react";
import { fetchContent, adminUpdateContent, adminResetContent, uploadImage } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Plus, Trash2, Upload, Loader2, Save, RotateCcw, X } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1";

const RacingEditor = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const bannerRef = useRef(null);
  const galleryRef = useRef(null);
  const { toast } = useToast();

  const load = () => fetchContent("racing").then(setC).catch(() => setC(null));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setArr = (k, i, patch) => setC((p) => ({ ...p, [k]: p[k].map((x, idx) => (idx === i ? (typeof x === "string" ? patch : { ...x, ...patch }) : x)) }));
  const addItem = (k, item) => setC((p) => ({ ...p, [k]: [...(p[k] || []), item] }));
  const removeItem = (k, i) => setC((p) => ({ ...p, [k]: p[k].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...c };
      delete payload.key;
      await adminUpdateContent("racing", payload);
      toast({ title: "Racing page saved", description: "Your changes are now live." });
    } catch (err) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset the Racing page to the default content? This cannot be undone.")) return;
    const d = await adminResetContent("racing");
    setC(d);
    toast({ title: "Reset to default" });
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    setUploadingBanner(true);
    try {
      const res = await uploadImage(file);
      set("bannerImage", res.url);
      toast({ title: "Banner image updated" });
    } catch (e) {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploadingBanner(false);
      if (bannerRef.current) bannerRef.current.value = "";
    }
  };

  const uploadGallery = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const f of list) urls.push((await uploadImage(f)).url);
      setC((p) => ({ ...p, galleryImages: [...(p.galleryImages || []), ...urls] }));
      toast({ title: `${urls.length} photo(s) added` });
    } catch (e) {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploadingGallery(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  if (!c) return <div className="py-20 text-center text-gray-500">Loading Racing page…</div>;

  const Section = ({ title, children }) => (
    <div className="bg-white border border-black/10 p-6 mb-6">
      <h3 className="font-serif-display text-lg font-bold gpm-text-green mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl font-bold gpm-text-green">Racing Page</h2>
          <p className="text-sm text-gray-500">Edit the content shown on the public Racing page.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={reset} className="flex items-center gap-2 px-4 py-2.5 border border-black/20 text-sm font-semibold"><RotateCcw size={15} /> Reset</button>
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
          </button>
        </div>
      </div>

      <Section title="Banner">
        <div className="space-y-4">
          <div><label className={labelCls}>Title</label><input className={inputCls} value={c.bannerTitle || ""} onChange={(e) => set("bannerTitle", e.target.value)} /></div>
          <div><label className={labelCls}>Subtitle</label><textarea rows={2} className={inputCls} value={c.bannerSubtitle || ""} onChange={(e) => set("bannerSubtitle", e.target.value)} /></div>
          <div>
            <label className={labelCls}>Banner image</label>
            {c.bannerImage && <img src={c.bannerImage} alt="" className="w-full h-40 object-cover rounded border border-black/10 mb-3" />}
            <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBanner(e.target.files[0])} />
            <div className="flex gap-3">
              <button type="button" onClick={() => bannerRef.current?.click()} disabled={uploadingBanner} className="flex items-center gap-2 px-4 py-2 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
                {uploadingBanner ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
              </button>
              <input className={inputCls} placeholder="or paste image URL" value={c.bannerImage || ""} onChange={(e) => set("bannerImage", e.target.value)} />
            </div>
          </div>
        </div>
      </Section>

      <Section title="Story">
        <div className="space-y-4">
          <div><label className={labelCls}>Eyebrow (small heading)</label><input className={inputCls} value={c.storyEyebrow || ""} onChange={(e) => set("storyEyebrow", e.target.value)} /></div>
          <div><label className={labelCls}>Heading</label><input className={inputCls} value={c.storyTitle || ""} onChange={(e) => set("storyTitle", e.target.value)} /></div>
          <div>
            <label className={labelCls}>Paragraphs</label>
            <div className="space-y-3">
              {(c.storyParagraphs || []).map((p, i) => (
                <div key={i} className="flex gap-2">
                  <textarea rows={3} className={inputCls} value={p} onChange={(e) => setArr("storyParagraphs", i, e.target.value)} />
                  <button type="button" onClick={() => removeItem("storyParagraphs", i)} className="text-gray-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => addItem("storyParagraphs", "")} className="mt-2 flex items-center gap-1 text-sm gpm-text-green font-semibold"><Plus size={15} /> Add paragraph</button>
          </div>
        </div>
      </Section>

      <Section title="Stats (3 shown with icons)">
        <div className="space-y-3">
          {(c.stats || []).map((s, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className={inputCls} placeholder="Value (e.g. 50+)" value={s.value} onChange={(e) => setArr("stats", i, { value: e.target.value })} />
              <input className={inputCls} placeholder="Label" value={s.label} onChange={(e) => setArr("stats", i, { label: e.target.value })} />
              <button type="button" onClick={() => removeItem("stats", i)} className="text-gray-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addItem("stats", { value: "", label: "" })} className="mt-2 flex items-center gap-1 text-sm gpm-text-green font-semibold"><Plus size={15} /> Add stat</button>
      </Section>

      <Section title="Photo Gallery">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Eyebrow</label><input className={inputCls} value={c.galleryEyebrow || ""} onChange={(e) => set("galleryEyebrow", e.target.value)} /></div>
            <div><label className={labelCls}>Heading</label><input className={inputCls} value={c.galleryTitle || ""} onChange={(e) => set("galleryTitle", e.target.value)} /></div>
          </div>
          {(c.galleryImages || []).length > 0 && (
            <div className="grid grid-cols-4 gap-3">
              {c.galleryImages.map((img, i) => (
                <div key={i} className="relative group aspect-[4/3]">
                  <img src={img} alt="" className="w-full h-full object-cover rounded border border-black/10" />
                  <button type="button" onClick={() => removeItem("galleryImages", i)} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                  {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] gpm-bg-green text-white px-1.5 py-0.5 rounded font-semibold">Featured</span>}
                </div>
              ))}
            </div>
          )}
          <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadGallery(e.target.files)} />
          <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploadingGallery} className="flex items-center gap-2 px-4 py-2 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
            {uploadingGallery ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload photos
          </button>
          <p className="text-[11px] text-gray-400">The first photo is shown larger (featured). Large photos are optimised automatically (up to 25MB).</p>
        </div>
      </Section>

      <Section title="Call to action">
        <div className="space-y-4">
          <div><label className={labelCls}>Heading</label><input className={inputCls} value={c.ctaTitle || ""} onChange={(e) => set("ctaTitle", e.target.value)} /></div>
          <div><label className={labelCls}>Text</label><textarea rows={2} className={inputCls} value={c.ctaText || ""} onChange={(e) => set("ctaText", e.target.value)} /></div>
        </div>
      </Section>

      <div className="flex justify-end pb-6">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-6 py-3 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
        </button>
      </div>
    </div>
  );
};

export default RacingEditor;
